import classNames from 'classnames'
import React, { useMemo, useRef } from 'react'

import useOverflow from '@/hooks/useOverflow'
import { colors, type ColorVariant } from '@/utils/theme'

import './Flex.scss'

export type SpacersRangeType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
type CleaningTypes = 'auto' | 'none' | 'unset'
type SpacersType = SpacersRangeType | CleaningTypes
type RadiusType = 'small' | 'medium' | 'base' | 'large' | 'full'

interface SpacingProps {
  gap?: SpacersType
  gh?: SpacersType
  gv?: SpacersType
  m?: SpacersType
  mt?: SpacersType
  mb?: SpacersType
  ml?: SpacersType
  mr?: SpacersType
  mx?: SpacersType
  my?: SpacersType
  p?: SpacersType
  pt?: SpacersType
  pb?: SpacersType
  pl?: SpacersType
  pr?: SpacersType
  px?: SpacersType
  py?: SpacersType
}

interface FlexProps extends SpacingProps {
  component?: React.ElementType
  children?: React.ReactNode
  inline?: boolean
  justify?: React.CSSProperties['justifyContent']
  align?: React.CSSProperties['alignItems']
  grid?: boolean
  cols?: string
  wrap?: boolean
  grow?: boolean
  wfull?: boolean
  hfull?: boolean
  className?: string
  style?: React.CSSProperties
  fraction?: number
  color?: ColorVariant
  radius?: RadiusType
  stack?: boolean
  scroll?: 'horizontal' | 'vertical'
  onClick?: () => void
  debug?: boolean
}

const spacingProps = [
  'gap', 'gh', 'gv',
  'm', 'mt', 'mb', 'ml', 'mr', 'mx', 'my',
  'p', 'pt', 'pb', 'pl', 'pr', 'px', 'py',
] as const

function Flex({
  component: Component = 'div',
  children,
  inline = false,
  justify = 'flex-start',
  align = 'flex-start',
  grid = false,
  cols = '1fr 1fr',
  wrap = false,
  grow = false,
  wfull = false,
  hfull = false,
  className,
  style,
  fraction,
  color,
  radius,
  stack = false,
  scroll,
  onClick,
  debug,
  ...rest
}: FlexProps) {
  const ref = useRef(null)

  const overflowResult = useOverflow(ref, scroll)
  const hasOverflow = scroll ? overflowResult.hasOverflow : false
  const scrollStarted = scroll ? overflowResult.scrollStarted : false
  const scrollEnded = scroll ? overflowResult.scrollEnded : false

  const backgroundColor = useMemo(() => (color ? colors[color] : ''), [color])

  const computedStyle = useMemo<React.CSSProperties>(() => ({
    display: grid ? 'grid' : 'flex',
    gridTemplateColumns: grid ? cols : undefined,
    flexDirection: inline ? 'row' : 'column',
    justifyContent: justify,
    alignItems: align,
    flexWrap: wrap ? 'wrap' : 'nowrap',
    flexGrow: grow ? 1 : 0,
    flex: fraction ? `${fraction} ${fraction} 0` : (grow ? '1' : undefined),
    width: wfull ? '100%' : undefined,
    height: hfull ? '100%' : undefined,
    backgroundColor: debug
      ? `#${Math.floor(Math.random() * 16777215).toString(16)}`
      : backgroundColor,
    ...style,
  }), [grid, cols, inline, justify, align, wrap, grow, fraction, wfull, hfull, debug, backgroundColor, style])

  const spacingClasses = spacingProps
    .map(prop => rest[prop] && `${prop}-${rest[prop]}`)
    .filter(Boolean)

  return (
    <Component
      ref={ref}
      style={computedStyle}
      onClick={onClick}
      className={classNames(
        'flex',
        {
          'scroll-horizontal': scroll === 'horizontal' && hasOverflow,
          'scroll-vertical': scroll === 'vertical' && hasOverflow,
          'scroll-start': scrollStarted,
          'scroll-end': scrollEnded,
        },
        radius && `radius-${radius}`,
        { inline },
        { stack },
        spacingClasses,
        className
      )}
    >
      {children}
    </Component>
  )
}

export default Flex
